import {
  AuthApiPayload,
  AuthOrigin,
  MCPParams,
  OAuthParamsV2,
} from '../../shared/types';
import { startAuthFlow, StartAuthFlowResponse } from '../api/UnifiedAccountApi';
import { LowLevelAuthFlowConfig } from '../../shared/components/LowLevelAuthFlowView/LowLevelAuthFlowMachineDefinition';
import { AuthenticateError } from '../../shared/types/authErrors';
import {
  createBuildAuthPayload,
  createAuthorizeUrlFetcher,
  createReconnectAuthorizeUrlFetcher,
  createFinishNewAccountHandler,
  createFinishReconnectHandler,
  createFinishMigrationReconnectHandler,
  resolveOAuthScopeSet,
} from './authFlowConfigHelpers';

export interface UnifiedAccountConfig {
  portalId: number;
  appId: number;
  appName?: string;
  appIcon?: string;
  /** @deprecated Use oAuthScopes instead */
  oAuthScope?: string;
  oAuthScopes?: string[];
  /** The surface this button is rendered on. */
  origin?: AuthOrigin;
  mcpParams?: MCPParams | null;
  incremental?: boolean;
  customParams?: Record<string, string>;
}

export interface AuthConfigFetchers<TAuthResponse = { accountId: number }> {
  forNewAccount: () => Promise<
    LowLevelAuthFlowConfig<AuthApiPayload<OAuthParamsV2>, TAuthResponse>
  >;
  forReconnect: (
    accountId: number
  ) => Promise<
    LowLevelAuthFlowConfig<AuthApiPayload<OAuthParamsV2>, TAuthResponse>
  >;
  forMigrationReconnect: (
    oAuthorAccountId?: string
  ) => Promise<
    LowLevelAuthFlowConfig<AuthApiPayload<OAuthParamsV2>, TAuthResponse>
  >;
}

export type CreateAuthConfigFetcher<
  TConfig extends UnifiedAccountConfig = UnifiedAccountConfig,
  TAuthResponse = { accountId: number },
> = (config: TConfig) => AuthConfigFetchers<TAuthResponse>;

async function startAndValidateAuthFlow(
  portalId: number,
  appId: number,
  appName?: string,
  origin?: AuthOrigin
): Promise<StartAuthFlowResponse & { '@result': 'OK' }> {
  const response = await startAuthFlow(portalId, { appId, appName, origin });

  if (response['@result'] !== 'OK') {
    throw new AuthenticateError(
      response.localizedErrorMessage,
      response.extraDetailsMessage
    );
  }

  return response;
}

export function createAuthConfigFetchers(
  config: UnifiedAccountConfig
): AuthConfigFetchers<{ accountId: number }> {
  const {
    portalId,
    appId,
    appName,
    appIcon = '',
    oAuthScope,
    oAuthScopes,
    origin,
    mcpParams,
    incremental,
    customParams,
  } = config;

  const resolvedOAuthScopeSet = resolveOAuthScopeSet(oAuthScope, oAuthScopes);

  return {
    forNewAccount: async () => {
      const {
        authenticationFlow,
        sessionId,
        appName: resolvedAppName,
      } = await startAndValidateAuthFlow(portalId, appId, appName, origin);

      return {
        appName: resolvedAppName,
        appIcon,
        appId,
        oAuthScopeSet: resolvedOAuthScopeSet,
        mcpParams: mcpParams ?? undefined,
        customParams,
        authFlow: { ...authenticationFlow, sessionId },
        buildAuthPayload: createBuildAuthPayload(appId),
        makeAuthorizeUrlDescriptor: createAuthorizeUrlFetcher({
          portalId,
          appId,
          sessionId,
        }),
        finishAuthFlow: createFinishNewAccountHandler({
          portalId,
          appId,
        }),
      };
    },

    forReconnect: async (accountId: number) => {
      const {
        authenticationFlow,
        sessionId,
        appName: resolvedAppName,
      } = await startAndValidateAuthFlow(portalId, appId, appName, origin);

      return {
        appName: resolvedAppName,
        appIcon,
        oAuthScopeSet: resolvedOAuthScopeSet,
        mcpParams: mcpParams ?? undefined,
        customParams,
        authFlow: { ...authenticationFlow, sessionId },
        buildAuthPayload: createBuildAuthPayload(appId),
        makeAuthorizeUrlDescriptor: createReconnectAuthorizeUrlFetcher({
          portalId,
          accountId,
          sessionId,
          incremental,
        }),
        finishAuthFlow: createFinishReconnectHandler({
          portalId,
          accountId,
          incremental,
        }),
      };
    },

    forMigrationReconnect: async (oAuthorAccountId?: string) => {
      const {
        authenticationFlow,
        sessionId,
        appName: resolvedAppName,
      } = await startAndValidateAuthFlow(portalId, appId, appName, origin);

      return {
        appName: resolvedAppName,
        appIcon,
        oAuthScopeSet: resolvedOAuthScopeSet,
        mcpParams: mcpParams ?? undefined,
        customParams,
        authFlow: { ...authenticationFlow, sessionId },
        buildAuthPayload: createBuildAuthPayload(appId),
        makeAuthorizeUrlDescriptor: createAuthorizeUrlFetcher({
          portalId,
          appId,
          sessionId,
        }),
        finishAuthFlow: createFinishMigrationReconnectHandler({
          portalId,
          appId,
          oAuthorAccountId,
        }),
      };
    },
  };
}
